// ---------------------------------------------------------------------------
// Source canvas — the Design tab's "Canvas" view.
//
// Every `.fig` surface candidate is laid out on one zoomable/pannable board at
// its true source size, rendered from the surface preview PNGs that the import
// already produced. Selecting a frame drives the same `selectedSurfaceId` the
// source tree and the frame editor use, so tree ↔ canvas ↔ editor stay in sync.
//
// Interaction model (no new dependency — the zoom/pan state machine mirrors
// what `features/flow-editor` gets from its canvas, expressed over the pure
// helpers in `openpencil-source-canvas-layout.ts`):
//   • wheel            → zoom anchored at the pointer
//   • drag             → pan (a drag never turns into a selection click)
//   • buttons/keyboard → zoom in/out, fit to screen
//   • off-screen frames do not mount their <img> (viewport culling)
// ---------------------------------------------------------------------------

import { Image as ImageIcon, Maximize2, Minus, Plus } from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { loadOpenPencilSurfacePreview } from "./openpencil-figma-client";
import type {
  OpenPencilFigmaSourceBundle,
  OpenPencilSurfaceCandidateFact,
  OpenPencilSurfacePreviewArtifact,
  OpenPencilSurfacePreviewFailure,
} from "./openpencil-figma-source";
import {
  availableCanvasViewportHeight,
  CANVAS_GROUP_GAP,
  CANVAS_GROUP_HEADER,
  CANVAS_MAX_SCALE,
  CANVAS_MIN_SCALE,
  canvasRowTargetWidth,
  centerCanvasViewportOnFrame,
  clampCanvasViewport,
  fitCanvasViewport,
  frameScreenRect,
  layoutSourceCanvas,
  panCanvasViewport,
  visibleCanvasFrameIds,
  wheelZoomFactor,
  zoomCanvasViewportAt,
  type CanvasSize,
  type CanvasViewport,
  type SourceCanvasFrame,
} from "./openpencil-source-canvas-layout";

const DRAG_THRESHOLD_PX = 4;
const BUTTON_ZOOM_STEP = 1.25;
/** Constant screen gap between a page-band label and its first frame row. */
const BAND_LABEL_OFFSET_PX = 34;

/**
 * Resolves a preview artifact to an <img> source. Local dev artifacts are
 * served straight off the Studio API; retained (R2) artifacts need the
 * authenticated blob fetch. `enabled` is the lazy-loading gate — a frame that
 * has never entered the viewport issues no request at all.
 */
export function useOpenPencilPreviewSource(
  bundle: OpenPencilFigmaSourceBundle,
  preview: OpenPencilSurfacePreviewArtifact | undefined,
  enabled: boolean,
): { source: string | null; failed: boolean } {
  const [source, setSource] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const artifactRef = preview?.artifactRef;
  const fileName = preview?.fileName;
  const sourceId = bundle.sourceId;
  const artifactKey = preview ? `${sourceId}::${artifactRef}::${fileName}` : "";
  const resolvedKeyRef = useRef("");
  const objectUrlRef = useRef<string | null>(null);

  // Object URLs outlive the culling gate on purpose: a frame that scrolls out
  // and back must not refetch, so the blob URL is released only on unmount.
  useEffect(
    () => () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    },
    [],
  );

  useEffect(() => {
    if (!enabled || !preview || !artifactRef || !fileName) return;
    if (resolvedKeyRef.current === artifactKey) return;
    resolvedKeyRef.current = artifactKey;
    setFailed(false);
    if (artifactRef.startsWith("local-artifact://openpencil/")) {
      setSource(
        `/api/openpencil/figma/artifacts/${encodeURIComponent(sourceId)}/previews/${encodeURIComponent(fileName)}`,
      );
      return;
    }
    const controller = new AbortController();
    void loadOpenPencilSurfacePreview(bundle, preview, controller.signal)
      .then((blob) => {
        if (controller.signal.aborted) return;
        if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = URL.createObjectURL(blob);
        setSource(objectUrlRef.current);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        resolvedKeyRef.current = "";
        setFailed(true);
      });
    return () => controller.abort();
    // `bundle` identity churns on every workspace commit; the artifact identity
    // (sourceId + artifactRef + fileName) is what actually addresses the bytes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, artifactKey]);

  return { source, failed };
}

interface CanvasFrameProps {
  bundle: OpenPencilFigmaSourceBundle;
  frame: SourceCanvasFrame;
  preview?: OpenPencilSurfacePreviewArtifact;
  failureReason?: string;
  selected: boolean;
  visible: boolean;
  scale: number;
  onSelect: (surfaceId: string) => void;
  onSelectAttempt: () => boolean;
}

function CanvasFrame({
  bundle,
  frame,
  preview,
  failureReason,
  selected,
  visible,
  scale,
  onSelect,
  onSelectAttempt,
}: CanvasFrameProps) {
  const { source, failed } = useOpenPencilPreviewSource(
    bundle,
    preview,
    visible,
  );
  const [imageBroken, setImageBroken] = useState(false);
  // Chrome/label metrics are divided by the board scale so they keep a constant
  // on-screen size the way Figma's frame labels do. That also means the label
  // has a fixed pixel budget: the frame name wins it, and the geometry/kind
  // chips only appear once the frame is wide enough on screen to hold them.
  const inverse = 1 / scale;
  const screenWidth = frame.width * scale;
  const showLabel = screenWidth >= 56;
  const showDimensions = screenWidth >= 170;
  const showKind = screenWidth >= 260;
  const missingReason = preview
    ? failed || imageBroken
      ? "Preview unavailable"
      : null
    : (failureReason ?? "No preview rendered for this frame");

  return (
    <div
      className="absolute"
      style={{
        left: frame.x,
        top: frame.y,
        width: frame.width,
        height: frame.height,
      }}
    >
      {showLabel ? (
        <div
          className="absolute left-0 flex items-center overflow-hidden whitespace-nowrap"
          style={{
            bottom: "100%",
            width: frame.width,
            gap: 5 * inverse,
            marginBottom: 6 * inverse,
            fontSize: 12 * inverse,
            lineHeight: 1.3,
          }}
        >
          <span
            className={`min-w-0 truncate font-medium ${selected ? "text-primary" : "text-muted-foreground"}`}
            title={frame.name}
          >
            {frame.name}
          </span>
          {showDimensions ? (
            <span className="shrink-0 text-muted-foreground/70">
              {Math.round(frame.sourceWidth)}×{Math.round(frame.sourceHeight)}
            </span>
          ) : null}
          {showKind ? (
            <span
              className="shrink-0 rounded-full border border-border text-muted-foreground/80"
              style={{ fontSize: 10 * inverse, padding: `0 ${5 * inverse}px` }}
            >
              {frame.classification === "mobile" ? "mobile" : "non-mobile"}
            </span>
          ) : null}
        </div>
      ) : null}
      <button
        type="button"
        data-testid={`openpencil-canvas-frame-${frame.surfaceId}`}
        data-selected={selected ? "true" : "false"}
        data-preview={source ? "loaded" : missingReason ? "missing" : "pending"}
        data-culled={visible ? "false" : "true"}
        aria-pressed={selected}
        aria-label={`${frame.name} — ${Math.round(frame.sourceWidth)} by ${Math.round(frame.sourceHeight)}, ${frame.classification}`}
        onClick={() => {
          if (!onSelectAttempt()) return;
          onSelect(frame.surfaceId);
        }}
        className="block h-full w-full overflow-hidden bg-background text-left"
        style={{
          boxShadow: selected
            ? `0 0 0 ${3 * inverse}px hsl(var(--primary))`
            : `0 0 0 ${1 * inverse}px hsl(var(--border))`,
        }}
      >
        {source && visible ? (
          <img
            src={source}
            alt={`${frame.name} source preview`}
            draggable={false}
            className="h-full w-full object-cover"
            onError={() => setImageBroken(true)}
          />
        ) : source ? (
          // Culled: the bytes stay resolved so panning back is instant, but the
          // image element itself is unmounted to keep a large board cheap.
          <div className="h-full w-full bg-muted/40" aria-hidden />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-muted/30">
            <ImageIcon
              className="text-muted-foreground/60"
              style={{ width: 20 * inverse, height: 20 * inverse }}
              aria-hidden
            />
            {missingReason ? (
              <span
                className="max-w-full px-2 text-center text-muted-foreground"
                style={{ fontSize: 11 * inverse, lineHeight: 1.4 }}
              >
                {missingReason}
              </span>
            ) : null}
          </div>
        )}
      </button>
    </div>
  );
}

/**
 * Nearest ancestor that actually scrolls. The Design tab's surface is a
 * `h-full overflow-auto` div, so that — not the window — is the box whose
 * remaining height the board has to fill. `null` when nothing scrolls (the
 * caller then falls back to the window).
 */
function findScrollContainer(node: HTMLElement): HTMLElement | null {
  let current = node.parentElement;
  while (current && current !== document.body) {
    const overflowY = getComputedStyle(current).overflowY;
    if (
      (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") &&
      current.clientHeight > 0
    ) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

interface Props {
  bundle: OpenPencilFigmaSourceBundle;
  surfaces: readonly OpenPencilSurfaceCandidateFact[];
  previews?: readonly OpenPencilSurfacePreviewArtifact[];
  previewFailures?: readonly OpenPencilSurfacePreviewFailure[];
  selectedSurfaceId: string;
  onSelectSurface: (surfaceId: string) => void;
  activePageId?: string;
}

export function OpenPencilSourceCanvas({
  bundle,
  surfaces,
  previews,
  previewFailures,
  selectedSurfaceId,
  onSelectSurface,
  activePageId,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState<CanvasSize>({ width: 0, height: 0 });
  const [viewport, setViewport] = useState<CanvasViewport>({
    scale: 1,
    x: 0,
    y: 0,
  });
  const viewportRef = useRef(viewport);
  viewportRef.current = viewport;
  const userAdjustedRef = useRef(false);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    moved: boolean;
  } | null>(null);
  const dragMovedRef = useRef(false);

  const layoutInput = useMemo(
    () =>
      surfaces.map((surface) => ({
        id: surface.id,
        name: surface.name,
        pageId: surface.pageId,
        pageName: surface.pageName,
        width: surface.width,
        height: surface.height,
        classification: surface.classification,
      })),
    [surfaces],
  );
  const rowTargetWidth = useMemo(
    () => canvasRowTargetWidth(layoutInput, size),
    [layoutInput, size],
  );
  const board = useMemo(
    () => layoutSourceCanvas(layoutInput, { rowTargetWidth }),
    [layoutInput, rowTargetWidth],
  );

  const previewBySurfaceId = useMemo(() => {
    const map = new Map<string, OpenPencilSurfacePreviewArtifact>();
    for (const preview of previews ?? []) map.set(preview.surfaceId, preview);
    return map;
  }, [previews]);
  const failureBySurfaceId = useMemo(() => {
    const map = new Map<string, string>();
    for (const failure of previewFailures ?? [])
      map.set(failure.surfaceId, failure.message);
    return map;
  }, [previewFailures]);

  // --- container measurement ------------------------------------------------
  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const read = () => {
      const rect = node.getBoundingClientRect();
      setSize((current) =>
        current.width === rect.width && current.height === rect.height
          ? current
          : { width: rect.width, height: rect.height },
      );
    };
    read();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const next = entry.contentRect;
      setSize((current) =>
        current.width === next.width && current.height === next.height
          ? current
          : { width: next.width, height: next.height },
      );
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // --- board height: fill whatever the first screen has left ------------------
  //
  // Replaces the old fixed `sm:h-[68vh]`. See the header of
  // `availableCanvasViewportHeight` for why a fixed height was wrong in both
  // directions. `null` = not measured yet → the CSS fallback height stands.
  const [fillHeight, setFillHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    const node = containerRef.current;
    if (typeof window === "undefined" || !node) return;

    const scroller = findScrollContainer(node);
    const measureFill = () => {
      const host = scroller ?? document.documentElement;
      const hostRect = host.getBoundingClientRect();
      const nodeRect = node.getBoundingClientRect();
      // Board offset inside the host's scrolled CONTENT: removing scrollTop is
      // what makes this independent of where the creator has scrolled to.
      const boardTop = nodeRect.top - hostRect.top + host.scrollTop;
      const next = availableCanvasViewportHeight({
        containerHeight: scroller ? host.clientHeight : window.innerHeight,
        boardTop,
      });
      setFillHeight((current) =>
        next === null || (current !== null && Math.abs(current - next) <= 1)
          ? current
          : next,
      );
    };

    measureFill();
    window.addEventListener("resize", measureFill);
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(() => measureFill());
      // The host gives window resizes; its content child gives every change in
      // the stack above the board (collapsing the "Design source" panel is
      // exactly that). The board's own growth also resizes that child, but the
      // recomputed value cannot change from it — `boardTop` does not depend on
      // the board's height — so the observer settles after one pass.
      if (scroller) {
        observer.observe(scroller);
        const content = scroller.firstElementChild;
        if (content) observer.observe(content);
      }
    }
    return () => {
      window.removeEventListener("resize", measureFill);
      observer?.disconnect();
    };
  }, []);

  const fitToScreen = useCallback(() => {
    const next = fitCanvasViewport(board, size);
    userAdjustedRef.current = false;
    setViewport(next);
  }, [board, size]);

  // Auto-fit while the operator has not taken manual control, and re-fit
  // whenever the board itself changes (new import / different surface set).
  const boardKey = `${board.width}x${board.height}x${board.frames.length}`;
  const lastBoardKeyRef = useRef<string>("");
  useEffect(() => {
    if (lastBoardKeyRef.current !== boardKey) {
      lastBoardKeyRef.current = boardKey;
      userAdjustedRef.current = false;
    }
    if (userAdjustedRef.current) return;
    setViewport(fitCanvasViewport(board, size));
  }, [board, boardKey, size]);

  const applyViewport = useCallback(
    (next: CanvasViewport) => {
      userAdjustedRef.current = true;
      setViewport(clampCanvasViewport(next, board, size));
    },
    [board, size],
  );

  const zoomBy = useCallback(
    (factor: number) => {
      applyViewport(
        zoomCanvasViewportAt(viewportRef.current, factor, {
          x: size.width / 2,
          y: size.height / 2,
        }),
      );
    },
    [applyViewport, size.height, size.width],
  );

  // Native (non-passive) wheel listener — React's synthetic wheel handler is
  // passive, so `preventDefault()` there would not stop the page from scrolling.
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = node.getBoundingClientRect();
      applyViewport(
        zoomCanvasViewportAt(
          viewportRef.current,
          wheelZoomFactor(event.deltaY, event.deltaMode),
          { x: event.clientX - rect.left, y: event.clientY - rect.top },
        ),
      );
    };
    node.addEventListener("wheel", onWheel, { passive: false });
    return () => node.removeEventListener("wheel", onWheel);
  }, [applyViewport]);

  // --- pan ------------------------------------------------------------------
  const onPointerDown = useCallback((event: ReactPointerEvent) => {
    if (event.button !== 0 && event.button !== 1) return;
    dragMovedRef.current = false;
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: viewportRef.current.x,
      originY: viewportRef.current.y,
      moved: false,
    };
  }, []);

  const onPointerMove = useCallback(
    (event: ReactPointerEvent) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      if (!drag.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
      if (!drag.moved) {
        drag.moved = true;
        dragMovedRef.current = true;
        (event.currentTarget as HTMLElement).setPointerCapture?.(
          event.pointerId,
        );
      }
      applyViewport(
        panCanvasViewport(
          { scale: viewportRef.current.scale, x: drag.originX, y: drag.originY },
          dx,
          dy,
        ),
      );
    },
    [applyViewport],
  );

  const endDrag = useCallback((event: ReactPointerEvent) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    if (drag.moved) {
      (event.currentTarget as HTMLElement).releasePointerCapture?.(
        event.pointerId,
      );
    }
  }, []);

  /** A frame click that ended a pan must not change the selection. */
  const acceptSelectionClick = useCallback(() => {
    if (dragMovedRef.current) {
      dragMovedRef.current = false;
      return false;
    }
    return true;
  }, []);

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent) => {
      if (event.key === "+" || event.key === "=") {
        event.preventDefault();
        zoomBy(BUTTON_ZOOM_STEP);
      } else if (event.key === "-" || event.key === "_") {
        event.preventDefault();
        zoomBy(1 / BUTTON_ZOOM_STEP);
      } else if (event.key === "0") {
        event.preventDefault();
        fitToScreen();
      } else if (event.key.startsWith("Arrow")) {
        event.preventDefault();
        const step = event.shiftKey ? 200 : 60;
        const dx =
          event.key === "ArrowLeft" ? step : event.key === "ArrowRight" ? -step : 0;
        const dy =
          event.key === "ArrowUp" ? step : event.key === "ArrowDown" ? -step : 0;
        applyViewport(panCanvasViewport(viewportRef.current, dx, dy));
      }
    },
    [applyViewport, fitToScreen, zoomBy],
  );

  // Selection arriving from elsewhere (source tree, surface list, mapper) pans
  // the off-screen frame into view without changing zoom.
  const lastRevealedRef = useRef<string>("");
  useEffect(() => {
    if (!selectedSurfaceId || selectedSurfaceId === lastRevealedRef.current)
      return;
    lastRevealedRef.current = selectedSurfaceId;
    if (size.width <= 0 || size.height <= 0) return;
    const frame = board.frames.find(
      (item) => item.surfaceId === selectedSurfaceId,
    );
    if (!frame) return;
    const rect = frameScreenRect(frame, viewportRef.current);
    const inside =
      rect.left >= 0 &&
      rect.top >= 0 &&
      rect.left + rect.width <= size.width &&
      rect.top + rect.height <= size.height;
    if (inside) return;
    setViewport(
      clampCanvasViewport(
        centerCanvasViewportOnFrame(viewportRef.current, frame, size),
        board,
        size,
      ),
    );
  }, [board, selectedSurfaceId, size]);

  const visibleIds = useMemo(
    () => new Set(visibleCanvasFrameIds(board.frames, viewport, size)),
    [board.frames, size, viewport],
  );

  // Band labels keep a constant on-screen size while the gap that separates
  // two bands is board-space: below this zoom the label of band N would land
  // on top of band N-1's frames, so it is dropped instead.
  const bandLabelFits =
    (CANVAS_GROUP_GAP + CANVAS_GROUP_HEADER) * viewport.scale >=
    BAND_LABEL_OFFSET_PX + 10;

  const renderedCount = board.frames.filter((frame) =>
    previewBySurfaceId.has(frame.surfaceId),
  ).length;
  const zoomPercent = Math.round(viewport.scale * 100);

  return (
    <div data-testid="openpencil-source-canvas" className="flex flex-col">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border pb-3">
        <div className="flex h-8 items-center overflow-hidden rounded-md border border-border bg-background">
          <button
            type="button"
            aria-label="Zoom out"
            title="Zoom out"
            disabled={viewport.scale <= CANVAS_MIN_SCALE}
            onClick={() => zoomBy(1 / BUTTON_ZOOM_STEP)}
            className="flex h-8 w-8 items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40"
          >
            <Minus className="h-3.5 w-3.5" />
          </button>
          <span
            data-testid="openpencil-canvas-zoom-level"
            className="min-w-14 border-x border-border px-2 text-center text-xs tabular-nums text-foreground"
          >
            {zoomPercent}%
          </span>
          <button
            type="button"
            aria-label="Zoom in"
            title="Zoom in"
            disabled={viewport.scale >= CANVAS_MAX_SCALE}
            onClick={() => zoomBy(BUTTON_ZOOM_STEP)}
            className="flex h-8 w-8 items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
        <button
          type="button"
          aria-label="Fit to screen"
          onClick={fitToScreen}
          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-background px-2.5 text-xs font-medium text-foreground hover:bg-muted"
        >
          <Maximize2 className="h-3.5 w-3.5" aria-hidden />
          Fit to screen
        </button>
        <span
          className="text-[11px] text-muted-foreground"
          data-testid="openpencil-canvas-summary"
        >
          {board.frames.length} frames · {board.groups.length} source pages ·{" "}
          {renderedCount}/{board.frames.length} previews
        </span>
        <span className="ml-auto hidden text-[11px] text-muted-foreground sm:inline">
          Scroll to zoom · drag to pan · click a frame to select
        </span>
      </div>

      <div
        ref={containerRef}
        data-testid="openpencil-canvas-viewport"
        role="application"
        aria-label="Source canvas"
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={onKeyDown}
        data-fill-height={fillHeight === null ? "css" : String(fillHeight)}
        className="relative h-[360px] min-h-[360px] w-full touch-none select-none overflow-hidden rounded-md border border-border bg-[radial-gradient(hsl(var(--border))_1px,transparent_1px)] [background-size:16px_16px] outline-none focus-visible:ring-2 focus-visible:ring-primary sm:h-[68vh]"
        style={{
          cursor: "grab",
          ...(fillHeight === null ? {} : { height: fillHeight }),
        }}
      >
        <div
          data-testid="openpencil-canvas-board"
          className="absolute left-0 top-0 origin-top-left"
          style={{
            transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.scale})`,
            width: Math.max(board.width, 1),
            height: Math.max(board.height, 1),
          }}
        >
          {board.groups.map((group, groupIndex) => (
            // Zero-height anchor on the group's first frame row: the page label
            // then sits a constant number of screen pixels above the frames,
            // clearing their own labels at every zoom level.
            <div
              key={group.pageId}
              className="absolute"
              style={{
                left: group.x,
                top: group.y + CANVAS_GROUP_HEADER,
                width: Math.max(group.width, 1),
                height: 0,
              }}
            >
              {groupIndex === 0 || bandLabelFits ? (
                <div
                  className={`absolute left-0 truncate font-semibold ${
                    activePageId === group.pageId
                      ? "text-foreground"
                      : "text-muted-foreground/70"
                  }`}
                  style={{
                    bottom: BAND_LABEL_OFFSET_PX / viewport.scale,
                    fontSize: 14 / viewport.scale,
                    lineHeight: 1.2,
                    maxWidth: Math.max(group.width, 1),
                  }}
                >
                  {group.pageName}
                  <span className="font-normal text-muted-foreground/60">
                    {" "}
                    · {group.frameCount} frames
                  </span>
                </div>
              ) : null}
            </div>
          ))}
          {board.frames.map((frame) => (
            <CanvasFrame
              key={frame.surfaceId}
              bundle={bundle}
              frame={frame}
              preview={previewBySurfaceId.get(frame.surfaceId)}
              failureReason={failureBySurfaceId.get(frame.surfaceId)}
              selected={frame.surfaceId === selectedSurfaceId}
              visible={visibleIds.has(frame.surfaceId)}
              scale={viewport.scale}
              onSelect={onSelectSurface}
              onSelectAttempt={acceptSelectionClick}
            />
          ))}
        </div>
        {board.frames.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
            This source readback declared no frame candidates.
          </div>
        ) : null}
      </div>
    </div>
  );
}
